import { useState, useRef, useEffect } from 'react';
import { useSchemaDesignerStore } from '@/stores/schema-designer-store';
import { Button } from '@/components/ui/button';
import { generateThumbnail } from '@/lib/thumbnail-generator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RiShareLine, RiEyeLine, RiLoader4Line, RiCheckLine, RiCloseLine } from '@remixicon/react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// Predefined categories
const CATEGORIES = [
  'E-commerce',
  'SaaS',
  'Social Media',
  'Blog/CMS',
  'Analytics',
  'Auth',
  'Payments',
  'Messaging',
  'Starter',
  'Intermediate',
  'Advanced',
];

export function PublishDialog() {
  const { nodes, edges, currentSchemaId } = useSchemaDesignerStore();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [customCategory, setCustomCategory] = useState('');
  const [thumbnailDataUrl, setThumbnailDataUrl] = useState<string | null>(null);

  const canPublish = currentSchemaId && nodes.length > 0;

  // Auto-generate thumbnail when dialog opens
  useEffect(() => {
    if (open && !thumbnailDataUrl) {
      // Small delay to ensure the dialog is rendered
      const timer = setTimeout(() => {
        handleGenerateThumbnail();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleGenerateThumbnail = async () => {
    setGeneratingThumbnail(true);
    try {
      // Find the React Flow viewport element
      const reactFlowElement = document.querySelector('.react-flow__viewport')?.parentElement as HTMLElement;

      if (!reactFlowElement) {
        console.error('Could not find schema canvas for thumbnail');
        setGeneratingThumbnail(false);
        return;
      }

      const dataUrl = await generateThumbnail(reactFlowElement, {
        width: 1200,
        height: 630,
        quality: 0.9,
      });

      setThumbnailDataUrl(dataUrl);
    } catch (error) {
      console.error('Thumbnail generation error:', error);
      // Silent fail - thumbnail is optional
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handlePublish = async () => {
    if (!currentSchemaId) {
      toast.error('Please save your schema first before publishing');
      return;
    }

    if (!title.trim()) {
      toast.error('Please provide a title for your template');
      return;
    }

    if (selectedCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }

    setLoading(true);
    try {
      await api.publishSchema({
        schemaId: currentSchemaId,
        title: title.trim(),
        description: description.trim() || undefined,
        categories: selectedCategories,
        thumbnail: thumbnailDataUrl || undefined,
      });

      toast.success('Schema published successfully!');
      setOpen(false);

      // Reset form
      setTitle('');
      setDescription('');
      setSelectedCategories([]);
      setCustomCategory('');
      setThumbnailDataUrl(null);
    } catch (error: any) {
      console.error('Publish error:', error);
      toast.error(error.message || 'Failed to publish schema');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const addCustomCategory = () => {
    const trimmed = customCategory.trim();
    if (trimmed && !selectedCategories.includes(trimmed)) {
      setSelectedCategories((prev) => [...prev, trimmed]);
      setCustomCategory('');
    }
  };

  const removeCategory = (category: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Dialog open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                variant="default"
                size="sm"
                className="h-9 gap-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                disabled={!canPublish}
              >
                <RiShareLine className="h-4 w-4" />
                Publish
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {canPublish
              ? 'Publish this schema to the community gallery'
              : 'Save your schema first to enable publishing'}
          </TooltipContent>
        </Tooltip>

        <DialogContent className="sm:max-w-[550px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <RiShareLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Publish to Community Gallery
            </DialogTitle>
            <DialogDescription>
              Share your schema with the community. A preview thumbnail will be automatically generated from your schema design.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handlePublish();
            }}
            className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            <div className="space-y-4 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  placeholder="E-commerce Database Schema"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground">
                  A clear, descriptive title for your schema template
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="A complete database schema for e-commerce applications with products, orders, customers, and payment tracking..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  Optional: Describe what your schema is for and its key features
                </p>
              </div>

              {/* Categories */}
              <div className="space-y-2">
                <Label>
                  Categories <span className="text-destructive">*</span>
                </Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/20">
                  {CATEGORIES.map((category) => (
                    <Badge
                      key={category}
                      variant={selectedCategories.includes(category) ? 'default' : 'outline'}
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                      onClick={() => toggleCategory(category)}
                    >
                      {category}
                      {selectedCategories.includes(category) && (
                        <RiCheckLine className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>

                {/* Selected Categories */}
                {selectedCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-background">
                    <span className="text-xs text-muted-foreground w-full mb-1">
                      Selected ({selectedCategories.length}):
                    </span>
                    {selectedCategories.map((category) => (
                      <Badge key={category} variant="secondary" className="gap-1">
                        {category}
                        <button
                          type="button"
                          onClick={() => removeCategory(category)}
                          className="ml-1 hover:text-destructive"
                        >
                          <RiCloseLine className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Custom Category Input */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Add custom category..."
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomCategory();
                      }
                    }}
                    maxLength={30}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCustomCategory}
                    disabled={!customCategory.trim()}
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Select from predefined categories or add your own
                </p>
              </div>

              {/* Stats Preview */}
              <div className="flex items-center gap-4 p-3 border rounded-lg bg-muted/20">
                <div className="flex items-center gap-2 text-sm">
                  <RiEyeLine className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Preview:</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-medium">{nodes.length} tables</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="font-medium">{edges.length} relationships</span>
                </div>
              </div>
            </div>
          </form>

          <DialogFooter className="flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handlePublish}
              disabled={loading || !title.trim() || selectedCategories.length === 0}
              className="gap-2"
            >
              {loading ? (
                <>
                  <RiLoader4Line className="h-4 w-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <RiShareLine className="h-4 w-4" />
                  Publish Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
